// src/pages/Configuracoes.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    UserCircleIcon,
    ComputerDesktopIcon,
    KeyIcon,
    ClipboardIcon,
    EyeIcon,
    EyeSlashIcon,
    ArrowPathIcon,
    ChevronDownIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

// Componente reutilizável para o card de seção (opcional: cabeçalho clicável para expandir/recolher)
const SettingsCard = ({
    title,
    icon,
    children,
    collapsible = false,
    defaultExpanded = true,
}) => {
    const [expanded, setExpanded] = useState(() => (collapsible ? defaultExpanded : true));
    const isOpen = !collapsible || expanded;

    const headerProps = collapsible
        ? {
              role: 'button',
              tabIndex: 0,
              'aria-expanded': expanded,
              'aria-label': expanded ? `Recolher seção ${title}` : `Expandir seção ${title}`,
              className:
                  'flex-row items-center gap-4 space-y-0 cursor-pointer rounded-t-lg transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              onClick: () => setExpanded((v) => !v),
              onKeyDown: (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpanded((v) => !v);
                  }
              },
          }
        : { className: 'flex-row items-center gap-4 space-y-0' };

    return (
        <Card>
            <CardHeader {...headerProps}>
                <div className="rounded-lg bg-primary/10 p-2 text-primary shrink-0">
                    {React.cloneElement(icon, { className: 'h-6 w-6' })}
                </div>
                <CardTitle className="flex-1 min-w-0">{title}</CardTitle>
                {collapsible && (
                    <ChevronDownIcon
                        className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`}
                        aria-hidden
                    />
                )}
            </CardHeader>
            {isOpen && <CardContent className="space-y-4">{children}</CardContent>}
        </Card>
    );
};

export default function Configuracoes() {
    const [openaiKey, setOpenaiKey] = useState('');
    const [openaiMasked, setOpenaiMasked] = useState('');
    const [openaiLoading, setOpenaiLoading] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);
    const [userInfo, setUserInfo] = useState({ nome: '', email: '' });
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const userId = userData._id || userData.id;
    const tokenClient = localStorage.getItem('tokenClient') || '';
    const orgID = localStorage.getItem('orgID') || '';
    const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net').replace(/\/+$/, '');
    // const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/';  

    const [isSuperAdmin] = useState(userData.role === 'super_admin');
    const [isAdmin] = useState(userData.role === 'admin');
    const [tenants, setTenants] = useState([]);
    const [selectedTenantId, setSelectedTenantId] = useState('');
    const [branding, setBranding] = useState({
        brandName: '',
        logoUrl: '',
        theme: {
            primaryColor: '#4f46e5',
            secondaryColor: '#22c55e',
            backgroundColor: '#020817',
            textColor: '#f9fafb',
        },
    });
    const [brandingLoading, setBrandingLoading] = useState(false);
    const [followUpConfig, setFollowUpConfig] = useState({
        habilitado: false,
        maxTentativasTotal: 4,
        maxTentativasPorDia: 1,
        intervaloMinutos: 1440,
        horaInicioDisparos: '08:00',
        horaFimDisparos: '18:00',
        reativarCicloAposLimiteAtivo: false,
        reativarCicloQuantidade: 0,
        reativarCicloUnidade: 'semanas',
        numerosExcluidos: '',
        assistenteFollowUpId: '',
        phoneNumberFollowUpId: '',
        uazapiToken: '',
        uazapiMensagemLimite: '',
        uazapiMensagensNaoAtendeu: ['']
    });
    const [followUpLoading, setFollowUpLoading] = useState(false);
    const [restartFollowUpNumber, setRestartFollowUpNumber] = useState('');
    const [restartFollowUpLoading, setRestartFollowUpLoading] = useState(false);
    const [followUpSaveStatus, setFollowUpSaveStatus] = useState('idle'); // idle | saved | error
    const [followUpLastSavedAt, setFollowUpLastSavedAt] = useState(null);
    const [followUpDirty, setFollowUpDirty] = useState(false);
    const [showAllNaoAtendeuMessages, setShowAllNaoAtendeuMessages] = useState(false);
    const [assistentes, setAssistentes] = useState([]);
    const [phoneNumbers, setPhoneNumbers] = useState([]);

    const getAuthConfig = () => {
        const token = localStorage.getItem('token');
        if (!token) return null;
        return {
            headers: { Authorization: `Bearer ${token}` }
        };
    };

    useEffect(() => {
        const loadData = async () => {
            if (!userId) return;
            const config = getAuthConfig();
            if (!config) return;
            try {
                // Para usuário comum, evita endpoint restrito (/api/usuarios/:id retorna 403).
                if (isAdmin || isSuperAdmin) {
                    const userRes = await axios.get(`${baseUrl}/api/usuarios/${userId}`, config);
                    if (userRes.data) {
                        setUserInfo({
                            nome: userRes.data.nome || '',
                            email: userRes.data.email || ''
                        });
                    }
                } else {
                    setUserInfo({
                        nome: userData.nome || userData.name || '',
                        email: userData.email || ''
                    });
                }
                // Buscar chave OpenAI
                const keyRes = await axios.get(`${baseUrl}/api/usuarios/${userId}/openai-key`, config);
                setOpenaiMasked(keyRes.data.masked || '');
                const [followUpRes, assistentesRes, phonesRes] = await Promise.all([
                    axios.get(`${baseUrl}/api/usuarios/${userId}/followup-global-config`, config).catch(() => ({ data: { followUpGlobalConfig: null } })),
                    axios.get(`${baseUrl}/api/assistentes/master`, config).catch(() => ({ data: [] })),
                    axios.get(`${baseUrl}/api/phoneNumbers`, config).catch(() => ({ data: [] })),
                ]);
                if (followUpRes.data?.followUpGlobalConfig) {
                    setFollowUpConfig({
                        habilitado: !!followUpRes.data.followUpGlobalConfig.habilitado,
                        maxTentativasTotal: followUpRes.data.followUpGlobalConfig.maxTentativasTotal ?? 4,
                        maxTentativasPorDia: followUpRes.data.followUpGlobalConfig.maxTentativasPorDia ?? 1,
                        intervaloMinutos: followUpRes.data.followUpGlobalConfig.intervaloMinutos ?? 1440,
                        horaInicioDisparos: followUpRes.data.followUpGlobalConfig.horaInicioDisparos || '08:00',
                        horaFimDisparos: followUpRes.data.followUpGlobalConfig.horaFimDisparos || '18:00',
                        reativarCicloAposLimiteAtivo: followUpRes.data.followUpGlobalConfig.reativarCicloAposLimiteAtivo === true,
                        reativarCicloQuantidade: Number(followUpRes.data.followUpGlobalConfig.reativarCicloQuantidade || 0),
                        reativarCicloUnidade: followUpRes.data.followUpGlobalConfig.reativarCicloUnidade || 'semanas',
                        numerosExcluidos: Array.isArray(followUpRes.data.followUpGlobalConfig.numerosExcluidos)
                            ? followUpRes.data.followUpGlobalConfig.numerosExcluidos.join('\n')
                            : '',
                        assistenteFollowUpId: followUpRes.data.followUpGlobalConfig.assistenteFollowUpId || '',
                        phoneNumberFollowUpId: followUpRes.data.followUpGlobalConfig.phoneNumberFollowUpId || '',
                        uazapiToken: followUpRes.data.followUpGlobalConfig.uazapiToken || '',
                        uazapiMensagemLimite: followUpRes.data.followUpGlobalConfig.uazapiMensagemLimite || '',
                        uazapiMensagensNaoAtendeu: Array.isArray(followUpRes.data.followUpGlobalConfig.uazapiMensagensNaoAtendeu)
                            ? (
                                followUpRes.data.followUpGlobalConfig.uazapiMensagensNaoAtendeu.length
                                    ? followUpRes.data.followUpGlobalConfig.uazapiMensagensNaoAtendeu
                                    : ['']
                            )
                            : ['']
                    });
                    setFollowUpSaveStatus('saved');
                    setFollowUpDirty(false);
                }
                setAssistentes(Array.isArray(assistentesRes.data) ? assistentesRes.data : []);
                setPhoneNumbers(Array.isArray(phonesRes.data) ? phonesRes.data : []);

                // Se for super_admin, carregar tenants
                if (isSuperAdmin) {
                    const tenantsRes = await axios.get(`${baseUrl}/api/tenants`, config);
                    setTenants(Array.isArray(tenantsRes.data) ? tenantsRes.data : []);
                }
            } catch (err) {
                console.error('Erro ao buscar dados:', err);
                if (err.response?.status === 403 && isSuperAdmin) {
                    toast.error('Sem permissão para listar tenants.');
                }
            }
        };
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    // Recarregar lista de tenants ao abrir a página (super_admin) para pegar recém-criados
    const fetchTenants = useCallback(async () => {
        const ud = JSON.parse(localStorage.getItem('userData') || '{}');
        if (ud.role !== 'super_admin') return;
        const config = getAuthConfig();
        if (!config) return;
        try {
            const tenantsRes = await axios.get(`${baseUrl}/api/tenants`, config);
            setTenants(Array.isArray(tenantsRes.data) ? tenantsRes.data : []);
        } catch (err) {
            console.error('Erro ao buscar tenants:', err);
            toast.error('Erro ao carregar lista de clientes.');
        }
    }, [baseUrl]);

    useEffect(() => {
        if (userData.role === 'super_admin') {
            fetchTenants();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSaveOpenAIKey = async () => {
        if (!userId) return;
        const config = getAuthConfig();
        if (!config) return;
        if (!openaiKey.trim()) {
            toast.error('Informe uma chave válida.');
            return;
        }
        setOpenaiLoading(true);
        try {
            const res = await axios.put(
                `${baseUrl}/api/usuarios/${userId}/openai-key`,
                { apiKey: openaiKey.trim() },
                config
            );
            setOpenaiMasked(res.data.masked || '');
            setOpenaiKey('');
            toast.success('Chave OpenAI salva com sucesso.');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erro ao salvar chave OpenAI.');
        } finally {
            setOpenaiLoading(false);
        }
    };

    const handleRemoveOpenAIKey = async () => {
        if (!userId) return;
        const config = getAuthConfig();
        if (!config) return;
        setOpenaiLoading(true);
        try {
            await axios.delete(`${baseUrl}/api/usuarios/${userId}/openai-key`, config);
            setOpenaiMasked('');
            setOpenaiKey('');
            toast.success('Chave OpenAI removida.');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erro ao remover chave OpenAI.');
        } finally {
            setOpenaiLoading(false);
        }
    };

    const handleCopyToClipboard = async (text, label) => {
        if (!text) {
            toast.error(`Nenhum ${label} disponível para copiar.`);
            return;
        }
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copiado para a área de transferência!`);
        } catch (err) {
            // Fallback para navegadores mais antigos
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                toast.success(`${label} copiado para a área de transferência!`);
            } catch (e) {
                toast.error('Erro ao copiar. Tente selecionar e copiar manualmente.');
            }
          // Evita NotFoundError caso algum script já tenha removido o node.
          try {
            textArea.remove(); // no-op se já estiver fora do DOM
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[Configuracoes] falha ao remover textarea', err);
          }
        }
    };

    const getDisplayApiKey = () => {
        if (!tokenClient) return '';
        if (showApiKey) {
            return tokenClient;
        }
        // Quando oculto, mostra apenas pontos
        return '•'.repeat(Math.min(32, tokenClient.length || 20));
    };

    const handleSelectTenant = async (tenantId) => {
        setSelectedTenantId(tenantId);
        if (!tenantId) return;
        const config = getAuthConfig();
        if (!config) return;
        setBrandingLoading(true);
        try {
            const res = await axios.get(`${baseUrl}/api/tenants/${tenantId}/branding`, config);
            const b = res.data?.branding || {};
            setBranding({
                brandName: b.brandName || '',
                logoUrl: b.logoUrl || '',
                theme: {
                    primaryColor: b.theme?.primaryColor || '#4f46e5',
                    secondaryColor: b.theme?.secondaryColor || '#22c55e',
                    backgroundColor: b.theme?.backgroundColor || '#020817',
                    textColor: b.theme?.textColor || '#f9fafb',
                },
            });
        } catch (err) {
            toast.error('Erro ao carregar branding do tenant.');
            console.error(err);
        } finally {
            setBrandingLoading(false);
        }
    };

    const handleBrandingChange = (field, value) => {
        if (field.startsWith('theme.')) {
            const key = field.split('.')[1];
            setBranding((prev) => ({
                ...prev,
                theme: {
                    ...prev.theme,
                    [key]: value,
                },
            }));
        } else {
            setBranding((prev) => ({
                ...prev,
                [field]: value,
            }));
        }
    };

    const handleSaveBranding = async () => {
        if (!selectedTenantId) {
            toast.error('Selecione um tenant para salvar o branding.');
            return;
        }
        const config = getAuthConfig();
        if (!config) return;
        setBrandingLoading(true);
        const toastId = toast.loading('Salvando branding do tenant...');
        try {
            await axios.put(
                `${baseUrl}/api/tenants/${selectedTenantId}/branding`,
                {
                    brandName: branding.brandName,
                    logoUrl: branding.logoUrl,
                    theme: branding.theme,
                },
                config
            );
            toast.success('Branding salvo com sucesso!', { id: toastId });
        } catch (err) {
            toast.error('Erro ao salvar branding do tenant.', { id: toastId });
            console.error(err);
        } finally {
            setBrandingLoading(false);
        }
    };

    const handleFollowUpChange = (e) => {
        const { name, value, type, checked } = e.target;
        const field = name.split('.')[1];
        const parsedValue = type === 'checkbox' ? checked : ['maxTentativasTotal', 'maxTentativasPorDia', 'intervaloMinutos', 'reativarCicloQuantidade'].includes(field) ? Number(value) : value;
        setFollowUpConfig((prev) => ({ ...prev, [field]: parsedValue }));
        setFollowUpDirty(true);
        setFollowUpSaveStatus('idle');
    };

    const handleNaoAtendeuMessageChange = (index, value) => {
        setFollowUpConfig((prev) => {
            const list = Array.isArray(prev.uazapiMensagensNaoAtendeu) ? [...prev.uazapiMensagensNaoAtendeu] : [''];
            list[index] = value;
            return { ...prev, uazapiMensagensNaoAtendeu: list };
        });
        setFollowUpDirty(true);
        setFollowUpSaveStatus('idle');
    };

    const addNaoAtendeuMessage = () => {
        setFollowUpConfig((prev) => {
            const list = Array.isArray(prev.uazapiMensagensNaoAtendeu) ? [...prev.uazapiMensagensNaoAtendeu] : [];
            list.push('');
            return { ...prev, uazapiMensagensNaoAtendeu: list };
        });
        setShowAllNaoAtendeuMessages(true);
        setFollowUpDirty(true);
        setFollowUpSaveStatus('idle');
    };

    const removeNaoAtendeuMessage = (index) => {
        setFollowUpConfig((prev) => {
            const list = Array.isArray(prev.uazapiMensagensNaoAtendeu) ? [...prev.uazapiMensagensNaoAtendeu] : [''];
            if (list.length <= 1) return { ...prev, uazapiMensagensNaoAtendeu: [''] };
            list.splice(index, 1);
            const nextList = list.length ? list : [''];
            if (nextList.length <= 2) {
                setShowAllNaoAtendeuMessages(false);
            }
            return { ...prev, uazapiMensagensNaoAtendeu: nextList };
        });
        setFollowUpDirty(true);
        setFollowUpSaveStatus('idle');
    };

    const handleSaveFollowUpGlobal = async () => {
        const config = getAuthConfig();
        if (!config) return;
        setFollowUpLoading(true);
        const toastId = toast.loading('Salvando configuração de Follow-up...');
        try {
            const payload = {
                followUpGlobalConfig: {
                    habilitado: !!followUpConfig.habilitado,
                    maxTentativasTotal: Number(followUpConfig.maxTentativasTotal || 4),
                    maxTentativasPorDia: Number(followUpConfig.maxTentativasPorDia || 1),
                    intervaloMinutos: Number(followUpConfig.intervaloMinutos || 1440),
                    horaInicioDisparos: followUpConfig.horaInicioDisparos || '08:00',
                    horaFimDisparos: followUpConfig.horaFimDisparos || '18:00',
                    reativarCicloAposLimiteAtivo: followUpConfig.reativarCicloAposLimiteAtivo === true,
                    reativarCicloQuantidade: Number(followUpConfig.reativarCicloQuantidade || 0),
                    reativarCicloUnidade: followUpConfig.reativarCicloUnidade || 'semanas',
                    numerosExcluidos: String(followUpConfig.numerosExcluidos || '')
                        .split(/\r?\n/)
                        .map((x) => x.trim())
                        .filter(Boolean),
                    assistenteFollowUpId: followUpConfig.assistenteFollowUpId || null,
                    phoneNumberFollowUpId: followUpConfig.phoneNumberFollowUpId || null,
                    uazapiToken: followUpConfig.uazapiToken?.trim() || null,
                    uazapiMensagemLimite: followUpConfig.uazapiMensagemLimite?.trim() || null,
                    uazapiMensagensNaoAtendeu: (Array.isArray(followUpConfig.uazapiMensagensNaoAtendeu)
                        ? followUpConfig.uazapiMensagensNaoAtendeu
                        : [])
                        .map((x) => x.trim())
                        .filter(Boolean)
                }
            };
            await axios.put(`${baseUrl}/api/usuarios/${userId}/followup-global-config`, payload, config);
            toast.success('Follow-up global salvo com sucesso!', { id: toastId });
            setFollowUpSaveStatus('saved');
            setFollowUpLastSavedAt(new Date());
            setFollowUpDirty(false);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erro ao salvar Follow-up global.', { id: toastId });
            setFollowUpSaveStatus('error');
        } finally {
            setFollowUpLoading(false);
        }
    };

    const handleRestartFollowUpGlobalForNumber = async () => {
        const config = getAuthConfig();
        if (!config) return;
        const numero = String(restartFollowUpNumber || '').trim();
        const numeroDigits = numero.replace(/\D/g, '');
        if (!numeroDigits || numeroDigits.length < 10) {
            toast.error('Informe um número válido para reiniciar o Follow-up.');
            return;
        }

        setRestartFollowUpLoading(true);
        const toastId = toast.loading('Reiniciando ciclo de Follow-up para o número informado...');
        try {
            const payload = { contatos: [numero] };
            const res = await axios.post(
                `${baseUrl}/api/usuarios/me/followup-global/reiniciar-ciclo`,
                payload,
                config
            );
            const reiniciados = Number(res.data?.totalReiniciados || 0);
            if (reiniciados > 0) {
                toast.success('Ciclo de Follow-up reiniciado para este número.', { id: toastId });
            } else {
                toast('Número não encontrado na fila global de Follow-up.', { id: toastId, icon: '⚠️' });
            }
            setRestartFollowUpNumber('');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erro ao reiniciar ciclo de Follow-up para o número.', { id: toastId });
        } finally {
            setRestartFollowUpLoading(false);
        }
    };
    
    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-semibold tracking-tight">Configurações</h1>
                <p className="text-sm text-muted-foreground">
                    Gerencie preferências, credenciais e aparência do sistema.
                </p>
            </div>

            <div className="mx-auto max-w-4xl space-y-6">
                {/* Card de Perfil */}
                <SettingsCard title="Perfil do Usuário" icon={<UserCircleIcon />}>
                    <div>
                        <Label htmlFor="nome">Nome</Label>
                        <Input id="nome" type="text" className="mt-1" value={userInfo.nome} readOnly />
                    </div>
                    <div>
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" type="email" className="mt-1" value={userInfo.email} readOnly />
                    </div>
                </SettingsCard>
                
                {/* Card de Aparência */}
                <SettingsCard title="Aparência" icon={<ComputerDesktopIcon />}>
                   <p className="text-sm text-muted-foreground">
                       Atualmente, o sistema utiliza um tema escuro padrão para garantir a melhor experiência visual e consistência entre as telas.
                   </p>
                </SettingsCard>
                
                {/* Card de Chaves de API */}
                <SettingsCard title="Chaves de API" icon={<KeyIcon />}>
                    <div className="space-y-4">
                        {/* Token Client (API Key) */}
                        <div className="space-y-2">
                            <Label htmlFor="tokenClient">API Key</Label>
                            <div className="flex gap-2">
                                <Input
                                    id="tokenClient"
                                    type="text"
                                    value={getDisplayApiKey()}
                                    readOnly
                                    className="mt-1 bg-muted flex-1 text-white"
                                    placeholder="Nenhuma API Key configurada"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    onClick={() => setShowApiKey(!showApiKey)}
                                    disabled={!tokenClient}
                                    className="mt-1"
                                    title={showApiKey ? "Ocultar API Key" : "Mostrar API Key"}
                                >
                                    {showApiKey ? (
                                        <EyeSlashIcon className="h-4 w-4" />
                                    ) : (
                                        <EyeIcon className="h-4 w-4" />
                                    )}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    onClick={() => handleCopyToClipboard(tokenClient, 'API Key')}
                                    disabled={!tokenClient}
                                    className="mt-1"
                                    title="Copiar API Key"
                                >
                                    <ClipboardIcon className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>

                        {/* OrgID */}
                        <div className="space-y-2">
                            <Label htmlFor="orgID">ID da Organização</Label>
                            <div className="flex gap-2">
                                <Input
                                    id="orgID"
                                    type="text"
                                    value={orgID}
                                    readOnly
                                    className="mt-1 bg-muted flex-1 text-white"
                                    placeholder="Nenhum ID de organização configurado"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    onClick={() => handleCopyToClipboard(orgID, 'ID da Organização')}
                                    disabled={!orgID}
                                    className="mt-1"
                                    title="Copiar ID da Organização"
                                >
                                    <ClipboardIcon className="h-4 w-4" />
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                ID da organização configurado pelo administrador.
                            </p>
                        </div>

                        {/* OpenAI API Key */}
                        <div className="space-y-2 border-t border-border pt-4">
                            <Label htmlFor="openaiKey">OpenAI API Key</Label>
                            <Input
                                id="openaiKey"
                                type="password"
                                value={openaiKey}
                                onChange={(e) => setOpenaiKey(e.target.value)}
                                placeholder={openaiMasked ? `Atual: ${openaiMasked}` : 'Cole sua chave OpenAI'}
                            />
                            <div className="flex flex-wrap gap-2">
                                <Button onClick={handleSaveOpenAIKey} disabled={openaiLoading}>
                                    Salvar chave
                                </Button>
                                <Button variant="secondary" onClick={handleRemoveOpenAIKey} disabled={openaiLoading || !openaiMasked}>
                                    Remover chave
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Sua chave é armazenada de forma segura e nunca exibida por completo.
                            </p>
                        </div>
                    </div>
                </SettingsCard>

                <SettingsCard
                    title="Follow-up Global"
                    icon={<ArrowPathIcon />}
                    collapsible
                    defaultExpanded={false}
                >
                    <div className="space-y-4">
                        {followUpSaveStatus === 'saved' && (
                            <div className="rounded-md border border-green-500/30 bg-green-500/10 px-3 py-2 text-sm text-green-300">
                                Configuração salva com sucesso
                                {followUpLastSavedAt ? ` às ${followUpLastSavedAt.toLocaleTimeString('pt-BR')}` : ''}.
                            </div>
                        )}
                        {followUpSaveStatus === 'error' && (
                            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                                Não foi possível salvar agora. Tente novamente.
                            </div>
                        )}
                        {followUpDirty && (
                            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
                                Existem alterações pendentes. Clique em "Salvar Follow-up Global".
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <input
                                id="followup.habilitado"
                                name="followup.habilitado"
                                type="checkbox"
                                checked={!!followUpConfig.habilitado}
                                onChange={handleFollowUpChange}
                            />
                            <Label htmlFor="followup.habilitado">Ativar Follow-up global para ligações fora de campanha</Label>
                        </div>

                        {followUpConfig.habilitado && (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <Label>Máximo total</Label>
                                        <Input name="followup.maxTentativasTotal" type="number" min="1" max="20" value={followUpConfig.maxTentativasTotal} onChange={handleFollowUpChange} className="mt-1" />
                                    </div>
                                    <div>
                                        <Label>Máximo por dia</Label>
                                        <Input name="followup.maxTentativasPorDia" type="number" min="1" max="10" value={followUpConfig.maxTentativasPorDia} onChange={handleFollowUpChange} className="mt-1" />
                                    </div>
                                    <div>
                                        <Label>Intervalo (minutos)</Label>
                                        <Input name="followup.intervaloMinutos" type="number" min="1" max="10080" value={followUpConfig.intervaloMinutos} onChange={handleFollowUpChange} className="mt-1" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label>Início dos disparos</Label>
                                        <Input name="followup.horaInicioDisparos" type="time" value={followUpConfig.horaInicioDisparos || '08:00'} onChange={handleFollowUpChange} className="mt-1" />
                                    </div>
                                    <div>
                                        <Label>Fim dos disparos</Label>
                                        <Input name="followup.horaFimDisparos" type="time" value={followUpConfig.horaFimDisparos || '18:00'} onChange={handleFollowUpChange} className="mt-1" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            id="followup.reativarCicloAposLimiteAtivo"
                                            name="followup.reativarCicloAposLimiteAtivo"
                                            type="checkbox"
                                            checked={!!followUpConfig.reativarCicloAposLimiteAtivo}
                                            onChange={handleFollowUpChange}
                                        />
                                        <Label htmlFor="followup.reativarCicloAposLimiteAtivo">Reativar ciclo após atingir limite</Label>
                                    </div>
                                    {followUpConfig.reativarCicloAposLimiteAtivo && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Label>Reativar após</Label>
                                                <Input name="followup.reativarCicloQuantidade" type="number" min="1" max="120" value={followUpConfig.reativarCicloQuantidade ?? 0} onChange={handleFollowUpChange} className="mt-1" />
                                            </div>
                                            <div>
                                                <Label>Unidade</Label>
                                                <select name="followup.reativarCicloUnidade" value={followUpConfig.reativarCicloUnidade || 'semanas'} onChange={handleFollowUpChange} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                                                    <option value="dias">Dias</option>
                                                    <option value="semanas">Semanas</option>
                                                    <option value="meses">Meses</option>
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label>Números excluídos do Follow-up</Label>
                                    <textarea
                                        name="followup.numerosExcluidos"
                                        value={followUpConfig.numerosExcluidos || ''}
                                        onChange={handleFollowUpChange}
                                        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        placeholder="Um número por linha. Esses contatos nunca entrarão no Follow-up."
                                        rows={4}
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label>Assistente de Follow-up</Label>
                                        <select name="followup.assistenteFollowUpId" value={followUpConfig.assistenteFollowUpId} onChange={handleFollowUpChange} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                                            <option value="">Usar assistente da ligação original</option>
                                            {assistentes.map((a) => (
                                                <option key={a.vapiId || a._id} value={a.vapiId || a._id}>{a.nome || a.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <Label>Número de Follow-up</Label>
                                        <select name="followup.phoneNumberFollowUpId" value={followUpConfig.phoneNumberFollowUpId} onChange={handleFollowUpChange} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                                            <option value="">Usar número da ligação original</option>
                                            {phoneNumbers.map((pn) => (
                                                <option key={pn.vapiId || pn._id} value={pn.vapiId || pn._id}>{pn.name} ({pn.number})</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label>Token da integração de mensagem</Label>
                                        <Input
                                            name="followup.uazapiToken"
                                            type="text"
                                            value={followUpConfig.uazapiToken || ''}
                                            onChange={handleFollowUpChange}
                                            className="mt-1"
                                            placeholder="Cole o token da integração de mensagem do cliente"
                                        />
                                    </div>
                                    <div>
                                        <Label>Mensagem ao atingir limite</Label>
                                        <textarea
                                            name="followup.uazapiMensagemLimite"
                                            value={followUpConfig.uazapiMensagemLimite || ''}
                                            onChange={handleFollowUpChange}
                                            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                            placeholder="Texto enviado quando o limite de tentativas for atingido (aceita quebra de linha)"
                                            rows={4}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Mensagens para cliente não atendido</Label>
                                    {(() => {
                                        const mensagens = Array.isArray(followUpConfig.uazapiMensagensNaoAtendeu)
                                            ? followUpConfig.uazapiMensagensNaoAtendeu
                                            : [''];
                                        const mensagensVisiveis = showAllNaoAtendeuMessages ? mensagens : mensagens.slice(0, 2);
                                        return mensagensVisiveis.map((msg, idx) => (
                                        <div key={`nao-atendeu-msg-${idx}`} className="rounded-md border border-input p-2 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-muted-foreground">Mensagem {idx + 1}</span>
                                                <Button
                                                    type="button"
                                                    variant="secondary"
                                                    onClick={() => removeNaoAtendeuMessage(idx)}
                                                    disabled={mensagens.length <= 1}
                                                >
                                                    Remover
                                                </Button>
                                            </div>
                                            <textarea
                                                value={msg}
                                                onChange={(e) => handleNaoAtendeuMessageChange(idx, e.target.value)}
                                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                placeholder="Escreva a mensagem (quebras de linha são permitidas)"
                                                rows={4}
                                            />
                                        </div>
                                        ));
                                    })()}
                                    {(Array.isArray(followUpConfig.uazapiMensagensNaoAtendeu) ? followUpConfig.uazapiMensagensNaoAtendeu.length : 0) > 2 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => setShowAllNaoAtendeuMessages((prev) => !prev)}
                                        >
                                            {showAllNaoAtendeuMessages ? 'Ver menos' : 'Ver mais'}
                                        </Button>
                                    )}
                                    <Button type="button" variant="outline" onClick={addNaoAtendeuMessage}>
                                        + Adicionar mensagem
                                    </Button>
                                </div>
                                <div className="space-y-2 border-t border-border pt-4">
                                    <Label>Reiniciar ciclo para número específico (Global)</Label>
                                    <div className="flex flex-col gap-2 md:flex-row">
                                        <Input
                                            type="text"
                                            value={restartFollowUpNumber}
                                            onChange={(e) => setRestartFollowUpNumber(e.target.value)}
                                            placeholder="Ex: +55 11 99999-9999"
                                            className="md:flex-1"
                                        />
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            disabled={restartFollowUpLoading}
                                            onClick={handleRestartFollowUpGlobalForNumber}
                                        >
                                            {restartFollowUpLoading ? 'Reiniciando...' : 'Reiniciar ciclo deste número'}
                                        </Button>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Use esta ação para resetar imediatamente as tentativas de Follow-up de um contato específico na fila global.
                                    </p>
                                </div>
                            </>
                        )}

                        <div>
                            <Button onClick={handleSaveFollowUpGlobal} disabled={followUpLoading}>
                                {followUpLoading ? 'Salvando...' : 'Salvar Follow-up Global'}
                            </Button>
                        </div>
                    </div>
                </SettingsCard>

                {/* Branding por Tenant (apenas super_admin) */}
                {isSuperAdmin && (
                    <SettingsCard title="Branding dos Clientes (White Label)" icon={<ComputerDesktopIcon />}>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="tenantSelect">Selecione o Tenant / Cliente</Label>
                                <select
                                    id="tenantSelect"
                                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={selectedTenantId}
                                    onChange={(e) => handleSelectTenant(e.target.value)}
                                >
                                    <option value="">Selecione...</option>
                                    {tenants.map((t) => (
                                        <option key={t._id} value={t._id}>
                                            {t.name} ({t.slug})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {selectedTenantId && (
                                <>
                                    <div>
                                        <Label htmlFor="brandName">Nome da marca</Label>
                                        <Input
                                            id="brandName"
                                            type="text"
                                            className="mt-1"
                                            value={branding.brandName}
                                            onChange={(e) => handleBrandingChange('brandName', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="logoUrl">URL da logo</Label>
                                        <Input
                                            id="logoUrl"
                                            type="text"
                                            className="mt-1"
                                            value={branding.logoUrl}
                                            onChange={(e) => handleBrandingChange('logoUrl', e.target.value)}
                                            placeholder="Ex: https://meudominio.com/logo.png"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <Label>Cor primária</Label>
                                            <div className="mt-1 flex items-center gap-2">
                                                <input
                                                    type="color"
                                                    value={branding.theme.primaryColor}
                                                    onChange={(e) => handleBrandingChange('theme.primaryColor', e.target.value)}
                                                    className="h-10 w-10 cursor-pointer rounded border border-border bg-transparent"
                                                />
                                                <Input
                                                    type="text"
                                                    value={branding.theme.primaryColor}
                                                    onChange={(e) => handleBrandingChange('theme.primaryColor', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <Label>Cor secundária</Label>
                                            <div className="mt-1 flex items-center gap-2">
                                                <input
                                                    type="color"
                                                    value={branding.theme.secondaryColor}
                                                    onChange={(e) => handleBrandingChange('theme.secondaryColor', e.target.value)}
                                                    className="h-10 w-10 cursor-pointer rounded border border-border bg-transparent"
                                                />
                                                <Input
                                                    type="text"
                                                    value={branding.theme.secondaryColor}
                                                    onChange={(e) => handleBrandingChange('theme.secondaryColor', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <Label>Cor de fundo</Label>
                                            <div className="mt-1 flex items-center gap-2">
                                                <input
                                                    type="color"
                                                    value={branding.theme.backgroundColor}
                                                    onChange={(e) => handleBrandingChange('theme.backgroundColor', e.target.value)}
                                                    className="h-10 w-10 cursor-pointer rounded border border-border bg-transparent"
                                                />
                                                <Input
                                                    type="text"
                                                    value={branding.theme.backgroundColor}
                                                    onChange={(e) => handleBrandingChange('theme.backgroundColor', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <Label>Cor do texto</Label>
                                            <div className="mt-1 flex items-center gap-2">
                                                <input
                                                    type="color"
                                                    value={branding.theme.textColor}
                                                    onChange={(e) => handleBrandingChange('theme.textColor', e.target.value)}
                                                    className="h-10 w-10 cursor-pointer rounded border border-border bg-transparent"
                                                />
                                                <Input
                                                    type="text"
                                                    value={branding.theme.textColor}
                                                    onChange={(e) => handleBrandingChange('theme.textColor', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="pt-2">
                                        <Button onClick={handleSaveBranding} disabled={brandingLoading}>
                                            {brandingLoading ? 'Salvando...' : 'Salvar branding do tenant'}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </SettingsCard>
                )}

                {/* Branding do próprio tenant (admin white label) */}
                {isAdmin && (
                    <SettingsCard title="Branding do Meu Painel (White Label)" icon={<ComputerDesktopIcon />}>
                        <div className="space-y-4">
                            <div>
                                <p className="text-sm text-muted-foreground mb-2">
                                    Altere a marca e as cores apenas do seu painel white label.
                                </p>
                            </div>

                            <div>
                                <Label htmlFor="myBrandName">Nome da marca</Label>
                                <Input
                                    id="myBrandName"
                                    type="text"
                                    className="mt-1"
                                    value={branding.brandName}
                                    onChange={(e) => handleBrandingChange('brandName', e.target.value)}
                                />
                            </div>
                            <div>
                                <Label htmlFor="myLogoFile">Logo do painel (PNG ou JPEG)</Label>
                                <input
                                    id="myLogoFile"
                                    type="file"
                                    accept="image/png,image/jpeg"
                                    className="mt-1 block w-full text-sm text-muted-foreground"
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const config = getAuthConfig();
                                        if (!config) return;
                                        const formData = new FormData();
                                        formData.append('logo', file);
                                        setBrandingLoading(true);
                                        const toastId = toast.loading('Enviando logo...');
                                        try {
                                            const res = await axios.post(
                                                `${baseUrl}/api/tenants/me/logo`,
                                                formData,
                                                {
                                                    headers: {
                                                        ...config.headers,
                                                        'Content-Type': 'multipart/form-data',
                                                    },
                                                }
                                            );
                                            const newLogoUrl = res.data?.logoUrl;
                                            if (newLogoUrl) {
                                                setBranding((prev) => ({
                                                    ...prev,
                                                    logoUrl: newLogoUrl,
                                                }));
                                            }
                                            toast.success('Logo enviada com sucesso! Recarregue a página para aplicar.', { id: toastId });
                                        } catch (err) {
                                            console.error(err);
                                            toast.error(err.response?.data?.message || 'Erro ao enviar logo.', { id: toastId });
                                        } finally {
                                            setBrandingLoading(false);
                                        }
                                    }}
                                />
                                {branding.logoUrl && (
                                    <p className="mt-1 text-xs text-muted-foreground break-all">
                                        Logo atual: {branding.logoUrl}
                                    </p>
                                )}
                            </div>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <Label>Cor primária</Label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={branding.theme.primaryColor}
                                            onChange={(e) => handleBrandingChange('theme.primaryColor', e.target.value)}
                                            className="h-10 w-10 cursor-pointer rounded border border-border bg-transparent"
                                        />
                                        <Input
                                            type="text"
                                            value={branding.theme.primaryColor}
                                            onChange={(e) => handleBrandingChange('theme.primaryColor', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label>Cor secundária</Label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={branding.theme.secondaryColor}
                                            onChange={(e) => handleBrandingChange('theme.secondaryColor', e.target.value)}
                                            className="h-10 w-10 cursor-pointer rounded border border-border bg-transparent"
                                        />
                                        <Input
                                            type="text"
                                            value={branding.theme.secondaryColor}
                                            onChange={(e) => handleBrandingChange('theme.secondaryColor', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label>Cor de fundo</Label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={branding.theme.backgroundColor}
                                            onChange={(e) => handleBrandingChange('theme.backgroundColor', e.target.value)}
                                            className="h-10 w-10 cursor-pointer rounded border border-border bg-transparent"
                                        />
                                        <Input
                                            type="text"
                                            value={branding.theme.backgroundColor}
                                            onChange={(e) => handleBrandingChange('theme.backgroundColor', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label>Cor do texto</Label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={branding.theme.textColor}
                                            onChange={(e) => handleBrandingChange('theme.textColor', e.target.value)}
                                            className="h-10 w-10 cursor-pointer rounded border border-border bg-transparent"
                                        />
                                        <Input
                                            type="text"
                                            value={branding.theme.textColor}
                                            onChange={(e) => handleBrandingChange('theme.textColor', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="pt-2 flex gap-3">
                                <Button
                                    onClick={async () => {
                                        const config = getAuthConfig();
                                        if (!config) return;
                                        setBrandingLoading(true);
                                        const toastId = toast.loading('Salvando branding do seu painel...');
                                        try {
                                            await axios.put(
                                                `${baseUrl}/api/tenants/me/branding`,
                                                {
                                                    brandName: branding.brandName,
                                                    logoUrl: branding.logoUrl,
                                                    theme: branding.theme,
                                                },
                                                config
                                            );
                                            toast.success('Branding salvo com sucesso! Recarregue a página para aplicar o tema.', { id: toastId });
                                        } catch (err) {
                                            toast.error('Erro ao salvar branding do seu painel.', { id: toastId });
                                            console.error(err);
                                        } finally {
                                            setBrandingLoading(false);
                                        }
                                    }}
                                    disabled={brandingLoading}
                                >
                                    {brandingLoading ? 'Salvando...' : 'Salvar branding do meu painel'}
                                </Button>
                            </div>
                        </div>
                    </SettingsCard>
                )}
            </div>
        </div>
    );
}